/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Paper,
  MenuItem,
  TextField,
} from '@mui/material'
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material'
import { useOrgan } from '../../../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../../../layouts/PermanentSidebarMUI'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { entities } from '../../../../constants/entities'
import useBaseService from '../../../../hooks/services/useBaseService'
import { useNavigate } from 'react-router-dom'
import useLoader from '../../../../hooks/useLoader'
import useNotification from '../../../../hooks/useNotification'

export const UploadInvoiceModal = ({
  data,
  open,
  onClose,
  isReplacement = false,
  romaneioId: initialRomaneioId,
  romaneioLabel,
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { post: baseServicePost } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()

  // Estado local para o arquivo, garantindo controle total sobre o input file
  const [selectedFile, setSelectedFile] = useState(null)
  const [romaneios, setRomaneios] = useState([])
  const [romaneioId, setRomaneioId] = useState(initialRomaneioId ? String(initialRomaneioId) : '')
  const { get: baseServiceGet } = useBaseService(navigate)
  const baseServiceGetRef = useRef(baseServiceGet)

  useEffect(() => { baseServiceGetRef.current = baseServiceGet }, [baseServiceGet])

  useEffect(() => {
    let cancelled = false
    if (!open || !data?.id || initialRomaneioId) return () => { cancelled = true }
    baseServiceGetRef.current(entities.shipments.romaneio.get(data.id))
      .then((res) => {
        if (cancelled) return
        const result = res?.result || res?.romaneio || []
        const list = Array.isArray(result) ? result : result ? [result] : []
        setRomaneios(list)
        if (list.length === 1) setRomaneioId(String(list[0].id))
      })
      .catch(() => { if (!cancelled) setRomaneios([]) })
    return () => { cancelled = true }
  }, [open, data?.id, initialRomaneioId])

  useEffect(() => {
    setRomaneioId(initialRomaneioId ? String(initialRomaneioId) : '')
  }, [initialRomaneioId, open])

  // --- Mutação de Upload ---
  const uploadDocument = useMutation({
    mutationFn: async () => {
      showLoader()
      const formDataToSend = new FormData()
      formDataToSend.append('shipment_id', data?.id)
      formDataToSend.append('document_type', 'NOTA_FISCAL')
      formDataToSend.append('file', selectedFile)
      if (romaneioId) formDataToSend.append('romaneio_id', romaneioId)



      // Usa endpoint de replace se for substituição, senão usa create
      const endpoint = isReplacement
        ? entities.shipments.documents.replace(data?.id)
        : entities.shipments.documents.create(data?.id)

      // O navegador precisa definir o boundary do multipart no Content-Type.
      const response = await baseServicePost(endpoint, formDataToSend)
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['packing-list'])
      onClose()
      setSelectedFile(null)
    },
    onError: (error) => {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        'Erro desconhecido ao fazer upload.'
      showNotification('error', `Falha ao anexar: ${errorMsg}`)
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const handleSubmit = async () => {
    if (!selectedFile) {
      showNotification(
        'warning',
        'Por favor, selecione um arquivo para upload.'
      )
      return
    }

    // Validação básica de tamanho (ex: 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      showNotification('error', 'O arquivo excede o limite máximo de 10MB.')
      return
    }

    try {
      await uploadDocument.mutateAsync()
    } catch (error) {
      console.error('Erro no upload:', error)
    }
  }

  const { selectedOrgan } = useOrgan()
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  useEffect(() => {
    if (!open) {
      setSelectedFile(null)
    }
  }, [open])

  // Handler customizado para o input file, já que SimpleBody pode não lidar perfeitamente com type='file'
  const handleFileChange = (event) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const disabled =
    uploadDocument.isPending ||
    !selectedFile ||
    !romaneioId

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: primaryColor,
          color: '#FFFFFF',
          p: 2,
        }}>
        <DialogTitle sx={{ fontWeight: 'bold', p: 0 }}>
          Anexar Nota Fiscal
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <div className="container-fluid p-0">
          {/* ALERTA DE SUBSTITUIÇÃO */}
          {isReplacement && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'warning.50',
                borderLeft: '4px solid',
                borderColor: 'warning.main',
                borderRadius: 1,
              }}>
              <Typography
                variant="body2"
                color="warning.dark"
                sx={{ fontWeight: 600 }}>
                Atenção: Ao enviar um novo arquivo, a nota fiscal atual será
                arquivada no histórico e substituída por esta.
              </Typography>
            </Box>
          )}

          {initialRomaneioId ? <Typography sx={{ mt: 2 }}><strong>Romaneio:</strong> {romaneioLabel || `#${initialRomaneioId}`}</Typography> : <TextField select fullWidth size="small" sx={{ mt: 2 }} label="Romaneio / Cliente" value={romaneioId} onChange={(event) => setRomaneioId(event.target.value)}>
            <MenuItem value=""><em>Selecione o romaneio</em></MenuItem>
            {romaneios.map((romaneio) => <MenuItem key={romaneio.id} value={romaneio.id}>{romaneio.customer_name || `Movimento ${romaneio.movement_id}`} — {romaneio.type}</MenuItem>)}
          </TextField>}

          {/* Input de Arquivo Customizado para garantir funcionamento perfeito */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Arquivo da Nota Fiscal (PDF, XML, JPG ou PNG - Máx 10MB)
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                width: '100%',
                p: 2,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderColor: selectedFile ? 'success.main' : 'grey.400',
                bgcolor: selectedFile ? 'success.50' : 'grey.50',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50',
                },
              }}
              component="label">
              <input
                type="file"
                hidden
                accept=".pdf,.xml,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              <CloudUploadIcon
                sx={{
                  fontSize: 40,
                  color: selectedFile ? 'success.main' : 'action.active',
                  mb: 1,
                }}
              />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedFile
                  ? selectedFile.name
                  : 'Clique para selecionar o arquivo'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedFile
                  ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                  : 'Arraste ou clique aqui'}
              </Typography>
            </Paper>

          </Box>
        </div>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={isReplacement ? 'warning' : 'primary'}
          disabled={disabled}
          startIcon={
            uploadDocument.isLoading ? (
              <Box
                sx={{
                  animation: 'spin 1s linear infinite',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  border: '2px solid white',
                  borderTopColor: 'transparent',
                }}
              />
            ) : (
              <CloudUploadIcon />
            )
          }>
          {uploadDocument.isLoading
            ? 'Processando...'
            : isReplacement
              ? 'Substituir Arquivo'
              : 'Anexar Nota Fiscal'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
